import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// Icon map for back destinations
const DEST_ICONS = {
  '/':            { icon: '🍽', color: '#3B5BDB', bg: '#F0F4FF', border: '#C7D7FD' },
  menu:           { icon: '📝', color: '#1565C0', bg: '#E3F2FD', border: '#90CAF9' },
  'price-list':   { icon: '📋', color: '#6A1B9A', bg: '#F3E5F5', border: '#CE93D8' },
  qr:             { icon: '⬛', color: '#1B5E20', bg: '#E8F5E9', border: '#A5D6A7' },
  slides:         { icon: '🎨', color: '#E65100', bg: '#FFF3E0', border: '#FFCC80' },
  branches:       { icon: '📍', color: '#15803D', bg: '#F0FDF4', border: '#86EFAC' },
  'info-screens': { icon: '🪧', color: '#0F766E', bg: '#ECFDF5', border: '#5EEAD4' },
};

/**
 * BrandNav — drop-in replacement for the plain `← text` back button.
 *
 * Props:
 *   brandId   — current brand id (used by the switcher)
 *   subPath   — current page type: 'menu' | 'price-list' | 'qr' | 'slides' | ...
 *               used to navigate to the same page for the newly selected brand
 *   backLabel — text shown on the back button, e.g. "Brands" or "Menu Editor"
 *   backPath  — where the back button navigates, e.g. "/" or `/brands/5/menu`
 *   backKey   — key into DEST_ICONS for colour/icon; defaults to subPath of backPath
 */
export default function BrandNav({ brandId, subPath, backLabel, backPath, backKey }) {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get('/brands').then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const handleSwitch = e => {
    const id = e.target.value;
    if (id && String(id) !== String(brandId)) navigate(`/brands/${id}/${subPath}`);
  };

  // Resolve icon/colours for the back destination
  const destKey = backKey || (backPath === '/' ? '/' : null) || subPath;
  const style   = DEST_ICONS[destKey] || DEST_ICONS['/'];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>

      {/* ── Styled back button — omitted on pages that are a destination,
           not a detour (Info Screens is reached from the sidebar). ── */}
      {backPath && <button
        onClick={() => navigate(backPath)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: style.bg, border: `1.5px solid ${style.border}`,
          borderRadius: 20, padding: '5px 13px 5px 9px',
          color: style.color, fontSize: 12, fontWeight: 700,
          cursor: 'pointer', transition: 'filter 0.15s',
          whiteSpace: 'nowrap', lineHeight: 1,
        }}
        onMouseOver={e => e.currentTarget.style.filter = 'brightness(0.94)'}
        onMouseOut={e => e.currentTarget.style.filter  = 'none'}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>←</span>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{style.icon}</span>
        <span>{backLabel}</span>
      </button>}

      {/* ── Brand switcher — admin only ── */}
      {brands.length > 1 && brandId && (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <select
            value={String(brandId)}
            onChange={handleSwitch}
            style={{
              appearance: 'none', WebkitAppearance: 'none',
              border: '1.5px solid #E0E0E0', borderRadius: 20,
              padding: '5px 30px 5px 12px', fontSize: 12, fontWeight: 600,
              color: '#333', background: '#FAFAFA', cursor: 'pointer',
              outline: 'none', lineHeight: 1,
            }}
          >
            {brands.map(b => (
              <option key={b.id} value={String(b.id)}>
                {b.name}{b.name_ar ? ' · ' + b.name_ar : ''}
                {b.owner_username ? ` (${b.owner_username})` : ''}
              </option>
            ))}
          </select>
          {/* Custom chevron */}
          <span style={{ position: 'absolute', right: 10, pointerEvents: 'none', color: '#999', fontSize: 10 }}>▼</span>
        </div>
      )}
    </div>
  );
}
